// Het venstertje dat de bookmarklet opent.
//
// Het staat op onze eigen site, dus het mag met de server praten en het heeft je gewone
// aanmelding. De pagina die je aan het lezen was komt hier binnen via `postMessage` — de enige
// weg die de Content-Security-Policy van een site niet kan dichtzetten. Zie bookmarklet.ts
// voor waarom dat nodig is.
//
// Komt er niets binnen (je opende dit adres zelf, of het venster stond al open), dan is er nog
// een tweede weg: `?url=` in het adres. Dan haalt de NAS de pagina zelf op.

import { useEffect, useRef, useState } from "react";
import { api, type ClipUitslag } from "../api.ts";
import { bewaarNotitie } from "../opslag.ts";
import { Icoon } from "./Icoon.tsx";
import { KLAAR, LADING } from "./bookmarklet.ts";

type Stand =
  | { soort: "wachten" }
  | { soort: "bezig"; titel: string }
  | { soort: "klaar"; uitslag: ClipUitslag }
  | { soort: "fout"; reden: string };

export function ClipOntvangst({ ga }: { ga: (pad: string) => void }) {
  const [stand, setStand] = useState<Stand>({ soort: "wachten" });
  // Eén pagina per venster: zonder deze grendel zou een site die twee keer stuurt ook twee
  // notities opleveren.
  const bezig = useRef(false);

  useEffect(() => {
    async function knip(invoer: { url: string; html?: string; toon?: string }) {
      if (bezig.current) return;
      bezig.current = true;
      setStand({ soort: "bezig", titel: invoer.toon || invoer.url });
      try {
        const uitslag = await api.clip({ url: invoer.url, html: invoer.html });
        await bewaarNotitie(uitslag.notitie);
        setStand({ soort: "klaar", uitslag });
      } catch (fout) {
        bezig.current = false;
        setStand({ soort: "fout", reden: (fout as Error).message });
      }
    }

    const ontvang = (bericht: MessageEvent) => {
      const lading = bericht.data as { soort?: string; url?: string; titel?: string; html?: string } | null;
      if (!lading || lading.soort !== LADING || !lading.url) return;
      // De titel van het tabblad ("Bakfiets - Wikipedia") is er een met de naam van de site
      // erachter geplakt. Hebben we de pagina zelf, dan haalt de leesmodus er een schonere uit
      // ("Bakfiets"); de tabtitel gebruiken we alleen om te laten zien waar hij mee bezig is.
      void knip({ url: lading.url, html: lading.html, toon: lading.titel });
    };
    addEventListener("message", ontvang);

    // "Ik sta klaar" — pas hierna stuurt de bookmarklet de pagina. Het venster weet niet van
    // welke site de klik kwam, dus dit bericht gaat naar iedereen; er staat dan ook niets in.
    // De pagina zelf komt de andere kant op, en die gaat alleen naar óns adres.
    opener?.postMessage({ soort: KLAAR }, "*");

    // Zelf geopend met een adres erin: dan is er geen bookmarklet en haalt de NAS hem op.
    const adres = new URLSearchParams(location.search).get("url");
    if (adres) void knip({ url: adres });

    return () => removeEventListener("message", ontvang);
  }, []);

  return (
    <div style={{ maxWidth: 520, margin: "0 auto", padding: "28px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 style={{ fontFamily: "var(--schrift-titel)", fontSize: 22, margin: 0 }}>Bewaren in Notities</h2>

      {stand.soort === "wachten" && (
        <p style={{ margin: 0, fontSize: 14, color: "var(--gedempt)" }}>
          Wachten op de pagina…
          <br />
          <span style={{ fontSize: 12 }}>
            Blijft dit staan, dan kwam je hier zonder de knop uit je bladwijzerbalk. Je kunt ook
            een adres meegeven: <code style={{ fontFamily: "var(--schrift-code)" }}>/clip?url=…</code>
          </span>
        </p>
      )}

      {stand.soort === "bezig" && (
        <p style={{ margin: 0, fontSize: 14, color: "var(--gedempt)", overflowWrap: "anywhere" }}>
          Bezig met <strong>{stand.titel}</strong> — de afbeeldingen komen mee, dus dit duurt een paar tellen.
        </p>
      )}

      {stand.soort === "klaar" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14 }}>
            <strong>{stand.uitslag.notitie.titel}</strong> staat in je Inbox
            {stand.uitslag.afbeeldingen > 0 && `, met ${stand.uitslag.afbeeldingen} afbeelding(en) erbij`}.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="knop hoofd" onClick={() => ga(`/notitie/${stand.uitslag.notitie.id}`)}>
              <Icoon naam="notitie" grootte={15} /> Openen
            </button>
            {/* Een venster dat zichzelf opende mag zichzelf ook sluiten; deed de browser dat
                niet, dan blijft er gewoon een tabblad met de notitie erin staan. */}
            <button className="knop stil" onClick={() => close()}>
              Sluiten
            </button>
          </div>
        </div>
      )}

      {stand.soort === "fout" && (
        <p style={{ margin: 0, fontSize: 13, padding: 10, borderRadius: 10, color: "#8c440f", background: "#fbf0e6" }} role="alert">
          {stand.reden}
        </p>
      )}
    </div>
  );
}
